import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, browserHistory } from 'react-router';
import moment from 'moment';
import Clear from 'material-ui/svg-icons/content/clear';
import Done from 'material-ui/svg-icons/action/done';
import { grey500, grey200, red300, lightGreen500 } from 'material-ui/styles/colors';
import 'react-table/react-table.css';
import FloatingActionButton from 'material-ui/FloatingActionButton';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import { translate } from 'react-i18next';

import { dateFromISO } from '../utils/helpers';
import CommonTable from '../components/CommonTable';
import AuthActionSelectFilterField from '../components/filters/AuthActionSelectFilterField';
import TextFilterField from '../components/filters/TextFilterField';
import YesNoFilterField from '../components/filters/YesNoFilterField';
import DateRangeFilterField from '../components/filters/DateRangeFilterField';
import * as endUserKeyActions from '../redux/modules/endUserKeys';
import ConfirmDialog from '../components/ConfirmDialog';
import i18n from '../translations/i18n';

/*
PropTypes / DefaultProps
 */
const defaultProps = {
  loading: false,
  endUserKeys: {},
};

const propTypes = {
  loading: PropTypes.bool,
  endUserKeys: PropTypes.object,
  fetchEndUserKeys: PropTypes.func,
  t: PropTypes.func,
  changeValidStatus: PropTypes.any,
};

class EndUserKeyList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      confirmDialogOpened: false,
      authorizationId: null,
    };

    this.defaultSorting = [
      {
        id: 'id',
        desc: true,
      },
    ];

    this.changeConfiguredStatus = this.changeConfiguredStatus.bind(this);
  }
  /*
    Lifecycle methods
   */
  componentWillMount() {
    this.props.fetchEndUserKeys();
  }

  /*
    Custom methods
   */

  openConfigurationDialog = (authorizationId) => {
    this.setState({
      confirmDialogOpened: true,
      authorizationId,
    });
  };

  cancelConfigurationDialog = () => {
    this.setState({
      confirmDialogOpened: false,
      authorizationId: null,
    });
  };

  changeConfiguredStatus = (endUserKeyId) => {
    this.props.changeValidStatus(endUserKeyId, false);

    this.cancelConfigurationDialog();
  };

  initColumns() {
    const styles = {
      floatingActionButton: {
        margin: 0,
        top: 'auto',
        right: 20,
        bottom: 20,
        left: 'auto',
        position: 'fixed',
      },
      editButton: {
        fill: grey500,
        height: 30,
        width: 30,
      },
      editButtonIcon: {
        height: 20,
        width: 20,
      },
    };

    this.columns = [
      {
        header: this.props.t('owner'),
        accessor: 'producer_full_name',
        render: (cellInfo) => {
          if (cellInfo.value && cellInfo.value !== ' ') {
            return <span>{cellInfo.value}</span>;
          }
          return <span>{cellInfo.row.producer_username}</span>;
        },
        filterMethod: (filter, row) =>
          row.producer_full_name.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField
            name="producer_full_name"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
      },
      {
        header: this.props.t('paketbox_name'),
        accessor: 'producer_paketbox_name',
        className: 'centerAlign',
        width: 140,
        render: obj => (
          <Link to={`/packetboxes/modify/${obj.row.paketbox___id}`}>
            {obj.row.producer_paketbox_name}
          </Link>
        ),
        filterMethod: (filter, row) =>
          row.producer_paketbox_name.toLowerCase().startsWith(filter.value.toLowerCase()),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField
            name="producer_paketbox_name"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
      },

      /*
          -- Address --
       */
      {
        header: this.props.t('street'),
        accessor: 'paketbox___street',
        filterMethod: (filter, row) => {
          if (!row.paketbox___street) return false;
          return row.paketbox___street.toLowerCase().indexOf(filter.value.toLowerCase()) >= 0;
        },
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="street" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('number'),
        accessor: 'paketbox___street_number',
        filterMethod: (filter, row) => {
          if (!row.paketbox___street_number) return false;
          return (
            row.paketbox___street_number.toLowerCase().indexOf(filter.value.toLowerCase()) >= 0
          );
        },
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="street_number" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('postal_code'),
        accessor: 'paketbox___postal_code',
        filterMethod: (filter, row) => {
          if (!row.paketbox___postal_code) return false;
          return row.paketbox___postal_code.toLowerCase().indexOf(filter.value.toLowerCase()) >= 0;
        },
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="postal_code" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: this.props.t('place'),
        accessor: 'paketbox___place',
        filterMethod: (filter, row) => {
          if (!row.paketbox___place) return false;

          return row.paketbox___place.toLowerCase().indexOf(filter.value.toLowerCase()) >= 0;
        },
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="place" filter={filter} onFilterChange={onFilterChange} />
        ),
      },

      /*
          -- Address END --
       */
      {
        header: this.props.t('valid'),
        show: true,
        accessor: 'valid',
        className: 'centerAlign',
        render: row => (row.value ? <Done color={lightGreen500} /> : <Clear color={red300} />),
        filterRender: ({ filter, onFilterChange }) => (
          <YesNoFilterField name="valid" filter={filter} onFilterChange={onFilterChange} />
        ),
        filterMethod: (filter, row) => row.valid === filter.value,
      },
      {
        header: this.props.t('valid_from'),
        accessor: 'valid_from',
        className: 'centerAlign',
        width: 220,
        render: row => dateFromISO(row.value) || '-',
        filterRender: ({ filter, onFilterChange }) => (
          <DateRangeFilterField name="valid_from" filter={filter} onFilterChange={onFilterChange} />
        ),
        filterMethod: (filter, row) => {
          if (!filter.value) return true;
          const startDate = filter.value.startDate.startOf('day');
          const endDate = filter.value.endDate.endOf('day');
          const rowDate = moment(row.valid_from);

          return rowDate.isAfter(startDate) && rowDate.isBefore(endDate);
        },
      },
      {
        header: this.props.t('valid_to'),
        accessor: 'valid_to',
        className: 'centerAlign',
        width: 220,
        render: row => dateFromISO(row.value) || '-',
        filterRender: ({ filter, onFilterChange }) => (
          <DateRangeFilterField name="valid_to" filter={filter} onFilterChange={onFilterChange} />
        ),
        filterMethod: (filter, row) => {
          if (!filter.value) return true;
          const startDate = filter.value.startDate.startOf('day');
          const endDate = filter.value.endDate.endOf('day');
          const rowDate = moment(row.valid_to);

          return rowDate.isAfter(startDate) && rowDate.isBefore(endDate);
        },
      },

      {
        header: this.props.t('authorized_user'),
        accessor: 'consumer_username',
        width: 200,
        render: (cellInfo) => {
          if (cellInfo.value && cellInfo.value !== ' ') {
            return <span>{cellInfo.value}</span>;
          }
          return <span>{cellInfo.row.consumer_username}</span>;
        },
        filterMethod: (filter, row) => {
          if (!row.consumer_username) return false;

          return row.consumer_username.toLowerCase().startsWith(filter.value.toLowerCase());
        },
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField
            name="consumer_full_name"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
      },
      {
        header: this.props.t('action'),
        accessor: 'action',
        className: 'centerAlign',
        render: row => this.props.t(`auth_action_${row.value}`),
        filterRender: ({ filter, onFilterChange }) => (
          <AuthActionSelectFilterField
            name="action"
            filter={filter}
            onFilterChange={onFilterChange}
          />
        ),
      },
      {
        header: this.props.t('paketbox_id'),
        show: false,
        accessor: 'paketbox___id',
        className: 'centerAlign',
        width: 60,
        filterMethod: (filter, row) =>
          parseInt(filter.value, 10) === parseInt(row.paketbox___id, 10),
        filterRender: ({ filter, onFilterChange }) => (
          <TextFilterField name="paketbox___id" filter={filter} onFilterChange={onFilterChange} />
        ),
      },
      {
        header: '',
        width: 60,
        sortable: false,
        hideFilter: true,
        className: 'centerAlign',
        render: (rowProps) => {
          const editPath = `/devices/modify/${rowProps.row.id}`;

          const showDelete = rowProps.row.valid && rowProps.row.action === 'ALL';

          return (
            <span>
              {showDelete && (
                <FloatingActionButton
                  zDepth={0}
                  mini
                  onTouchTap={() => {
                    this.openConfigurationDialog(rowProps.row.id);
                  }}
                  backgroundColor={grey200}
                  iconStyle={styles.editButton}
                >
                  <DeleteIcon style={styles.editButtonIcon} />
                </FloatingActionButton>
              )}
            </span>
          );
        },
      },
    ];
  }

  fetchData = () => {
    this.props.fetchEndUserKeys();
  };

  itemCreate = () => {
    browserHistory.push('/authorizations/new');
  };

  render() {
    this.initColumns();

    let data = [];
    if (this.props.endUserKeys) {
      data = Object.values(this.props.endUserKeys);
    }

    return (
      <div>
        <CommonTable
          title={this.props.t('authorizations')}
          data={data}
          loading={this.props.loading}
          columns={this.columns}
          onChange={this.fetchData}
          onItemCreate={this.itemCreate}
          defaultSorting={this.defaultSorting}
        />

        <ConfirmDialog
          open={this.state.confirmDialogOpened}
          text={this.props.t('key_invalidation_question')}
          onConfirm={() => {
            this.changeConfiguredStatus(this.state.authorizationId);
          }}
          onCancel={this.cancelConfigurationDialog}
        />
      </div>
    );
  }
}

EndUserKeyList.defaultProps = defaultProps;
EndUserKeyList.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    loading: state.endUserKeys.fetching,
    endUserKeys: state.endUserKeys.entities,
  };
}

const extended = translate(['translations'], { i18n })(EndUserKeyList);
export default connect(mapStateToProps, endUserKeyActions)(extended);



// WEBPACK FOOTER //
// ./src/containers/EndUserKeyList.js